import React, { useState } from 'react';
import StatCard from '../components/StatCard';
import DataTable from '../components/DataTable';
import useAPI from '../hooks/useAPI';
import { getPartsCatalog, getPartsForError } from '../services/api';

export default function SpareParts() {
  const { data, loading } = useAPI(getPartsCatalog);
  const [searchCode, setSearchCode] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);

  if (loading) return <div style={{ padding: 40, color: '#999' }}>Loading parts catalog...</div>;

  const parts = data?.parts || [];
  const categories = data?.by_category || [];
  const totalCost = data?.total_cost || 0;

  const handleSearch = async () => {
    if (!searchCode.trim()) return;
    setSearching(true);
    try {
      const result = await getPartsForError(searchCode.trim());
      setSearchResult(result);
    } catch {
      setSearchResult({ parts: [], error_code: searchCode });
    }
    setSearching(false);
  };

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Spare Parts Catalog</h1>
      <p style={{ fontSize: 14, color: '#636e72', marginTop: 4 }}>
        {parts.length} parts | Total inventory cost: ${totalCost.toLocaleString()}
      </p>

      {/* KPI Cards */}
      <div style={styles.cardGrid}>
        <StatCard label="Total Parts" value={parts.length} color="#6c5ce7" />
        <StatCard label="Categories" value={categories.length} color="#0984e3" />
        <StatCard label="Total Cost" value={`$${(totalCost / 1000).toFixed(0)}K`} color="#d63031" />
        <StatCard
          label="Highest Category"
          value={categories[0]?.category || '-'}
          sub={categories[0] ? `$${(categories[0].total_cost / 1000).toFixed(0)}K` : ''}
          color="#e17055"
        />
      </div>

      {/* Error code lookup */}
      <div style={styles.searchSection}>
        <h2 style={styles.sectionTitle}>Parts by Error Code</h2>
        <div style={styles.searchBar}>
          <input
            type="text"
            value={searchCode}
            onChange={e => setSearchCode(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Enter error code (e.g., E201)"
            style={styles.input}
          />
          <button onClick={handleSearch} disabled={searching} style={styles.searchBtn}>
            {searching ? 'Searching...' : 'Find Parts'}
          </button>
        </div>
        {searchResult && (
          <div style={{ marginTop: 12 }}>
            {searchResult.parts?.length > 0 ? (
              <DataTable
                columns={[
                  { key: 'part_id', label: 'Part ID' },
                  { key: 'description', label: 'Description' },
                  { key: 'category', label: 'Category' },
                  { key: 'unit_cost', label: 'Unit Cost', align: 'right', render: v => `$${v?.toFixed(2)}` },
                ]}
                data={searchResult.parts}
              />
            ) : (
              <div style={{ padding: 16, background: '#fff', borderRadius: 8, fontSize: 13, color: '#999', textAlign: 'center' }}>
                No parts found for error code "{searchResult.error_code}"
              </div>
            )}
          </div>
        )}
      </div>

      <div style={styles.twoCol}>
        {/* Cost by category */}
        <div>
          <h2 style={styles.sectionTitle}>Cost by Category</h2>
          {categories.map(cat => {
            const pct = totalCost > 0 ? ((cat.total_cost / totalCost) * 100).toFixed(1) : 0;
            return (
              <div key={cat.category} style={styles.catRow}>
                <div style={styles.catHeader}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{cat.category}</span>
                  <span style={{ fontSize: 12, color: '#636e72' }}>
                    ${(cat.total_cost / 1000).toFixed(1)}K ({pct}%)
                  </span>
                </div>
                <div style={styles.barBg}>
                  <div style={{ ...styles.barFill, width: `${pct}%` }} />
                </div>
                <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                  {cat.count} parts | Avg ${cat.count > 0 ? (cat.total_cost / cat.count).toFixed(0) : 0}/part
                </div>
              </div>
            );
          })}
        </div>

        {/* High-value parts */}
        <div>
          <h2 style={styles.sectionTitle}>High-Value Parts (&gt;$500)</h2>
          <DataTable
            columns={[
              { key: 'part_id', label: 'Part ID' },
              { key: 'description', label: 'Description' },
              { key: 'category', label: 'Category' },
              {
                key: 'unit_cost',
                label: 'Cost',
                align: 'right',
                render: v => (
                  <span style={{ fontWeight: 600, color: v >= 1000 ? '#d63031' : '#e17055' }}>
                    ${v?.toFixed(2)}
                  </span>
                ),
              },
              { key: 'quantity', label: 'Qty', align: 'right' },
              {
                key: 'total_cost',
                label: 'Total',
                align: 'right',
                render: (v, row) => `$${((row.unit_cost || 0) * (row.quantity || 1)).toLocaleString()}`,
              },
            ]}
            data={parts.filter(p => (p.unit_cost || 0) >= 500).slice(0, 20)}
          />
        </div>
      </div>
    </div>
  );
}

const styles = {
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
    margin: '20px 0',
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
  },
  sectionTitle: { fontSize: 16, fontWeight: 600, marginBottom: 12 },
  searchSection: {
    background: '#fff',
    borderRadius: 10,
    padding: 20,
    marginBottom: 24,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  searchBar: {
    display: 'flex',
    gap: 10,
  },
  input: {
    flex: 1,
    padding: '10px 14px',
    borderRadius: 6,
    border: '1px solid #ddd',
    fontSize: 14,
    outline: 'none',
  },
  searchBtn: {
    padding: '10px 20px',
    borderRadius: 6,
    border: 'none',
    background: '#6c5ce7',
    color: '#fff',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  },
  catRow: {
    padding: '12px 14px',
    background: '#fff',
    borderRadius: 8,
    marginBottom: 8,
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  },
  catHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  barBg: {
    height: 6,
    background: '#f0f0f0',
    borderRadius: 3,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    background: '#6c5ce7',
    borderRadius: 3,
    transition: 'width 0.3s',
  },
};
